import { safeGetData as get } from '@gumtree/shared/src/util/data-service';
import { Categories } from '@gumtree/shared/src/types/client-data';
import {
    getJobsPriceFilterOptions,
    getMotorsPriceFilterOptions,
    getRentPricePriceFilterOptions,
    getSalePricePriceFilterOptions,
    MAX_LABEL,
    MIN_LABEL,
    SelectOptionType,
} from '@gumtree/shared/src/constants/price-ranges';

import {
    FilterData,
    FiltersDictionary,
    FilterKey,
    RefinePanelModel,
    ServerRangedFilters,
    ServerFilters,
} from '../../types';

const minMotorsPriceFilterOptions: SelectOptionType[] = getMotorsPriceFilterOptions(true);
const maxMotorsPriceFilterOptions: SelectOptionType[] = getMotorsPriceFilterOptions(false);

const minJobsFilterOptions: SelectOptionType[] = getJobsPriceFilterOptions(true);
const maxJobsFilterOptions: SelectOptionType[] = getJobsPriceFilterOptions(false);

const minRentPriceFilterOptions: SelectOptionType[] = getRentPricePriceFilterOptions(true);
const maxRentPriceFilterOptions: SelectOptionType[] = getRentPricePriceFilterOptions(false);

const minSalePriceFilterOptions: SelectOptionType[] = getSalePricePriceFilterOptions(true);
const maxSalePriceFilterOptions: SelectOptionType[] = getSalePricePriceFilterOptions(false);

export const getBedsOptions = (isMin: boolean): SelectOptionType[] => {
    return [
        isMin ? { value: MIN_LABEL, label: MIN_LABEL } : { value: MAX_LABEL, label: MAX_LABEL },
        { value: '1', label: '1' },
        { value: '2', label: '2' },
        { value: '3', label: '3' },
        { value: '4', label: '4' },
        { value: '5', label: '5' },
        { value: '6', label: '6' },
        { value: '7', label: '7' },
        { value: '8', label: '8' },
        { value: '9', label: '9' },
        { value: '10', label: '10' },
    ];
};
const minNumbersBedsOptions: SelectOptionType[] = getBedsOptions(true);
const maxNumbersBedsOptions: SelectOptionType[] = getBedsOptions(false);

export const isTheCategoryJobs = (categories: Categories) => categories.l1Category === 'jobs';

export const isTheFilterFreeform = (categories: Categories) => {
    const { l1Category, l2Category } = categories;

    return (
        l1Category === 'for-sale' ||
        l2Category === 'pets-for-sale' ||
        l2Category === 'pet-equipment-accessories' ||
        l2Category === 'motors-parts' ||
        l2Category === 'motors-accessories'
    );
};

export const isTheCategoryVehicles = (categories: Categories) => {
    const { l2Category } = categories;

    return (
        l2Category === 'cars' ||
        l2Category === 'motorbikes-scooters' ||
        l2Category === 'vans' ||
        l2Category === 'caravans' ||
        l2Category === 'campervans-motorhomes' ||
        l2Category === 'trucks' ||
        l2Category === 'other-vehicles' ||
        l2Category === 'plant-tractors'
    );
};

export const isTheCategoryPropertiesToRent = (categories: Categories) => {
    const { l2Category, l3Category } = categories;

    return (
        l2Category === 'property-to-rent' ||
        l3Category === 'commercial-property-to-rent' ||
        l2Category === 'property-to-share' ||
        l3Category === 'garage-parking-to-rent' ||
        l2Category === 'holiday-rentals'
    );
};

export const isTheCategoryPropertiesForSale = (categories: Categories) => {
    const { l2Category, l3Category } = categories;

    return (
        l2Category === 'property-for-sale' ||
        l3Category === 'commercial-property-for-sale' ||
        l3Category === 'garage-parking-for-sale' ||
        l2Category === 'international-property-for-sale'
    );
};

export const isTheFilterADropDown = (categories: Categories) =>
    isTheCategoryVehicles(categories) ||
    isTheCategoryPropertiesToRent(categories) ||
    isTheCategoryPropertiesForSale(categories) ||
    isTheCategoryJobs(categories);

export const getMinSelectList = (categories: Categories) => {
    if (isTheCategoryVehicles(categories)) {
        return minMotorsPriceFilterOptions;
    } else if (isTheCategoryPropertiesToRent(categories)) {
        return minRentPriceFilterOptions;
    } else if (isTheCategoryPropertiesForSale(categories)) {
        return minSalePriceFilterOptions;
    } else if (isTheCategoryJobs(categories)) {
        return minJobsFilterOptions;
    } else {
        return [];
    }
};

export const getMaxSelectList = (categories: Categories) => {
    if (isTheCategoryVehicles(categories)) {
        return maxMotorsPriceFilterOptions;
    } else if (isTheCategoryPropertiesToRent(categories)) {
        return maxRentPriceFilterOptions;
    } else if (isTheCategoryPropertiesForSale(categories)) {
        return maxSalePriceFilterOptions;
    } else if (isTheCategoryJobs(categories)) {
        return maxJobsFilterOptions;
    } else {
        return [];
    }
};

export const shouldPriceFilterExistInCategory = (categories: Categories) =>
    !isTheCategoryJobs(categories) &&
    (isTheFilterFreeform(categories) || isTheFilterADropDown(categories));

export const overrideWithCustomDataSets = (
    [key, value]: [FilterKey, FilterData],
    categories: Categories
): [FilterKey, FilterData] => {
    if (key === 'salary') {
        return [
            key,
            {
                ...value,
                type: 'enum_range',
                minKey: `min_${key}`,
                maxKey: `max_${key}`,
                minValues: minJobsFilterOptions,
                maxValues: maxJobsFilterOptions,
            },
        ];
    } else if (key === ServerRangedFilters.property_number_beds) {
        return [
            key,
            {
                ...value,
                type: 'enum_range',
                minKey: `min_${key}`,
                maxKey: `max_${key}`,
                minValues: minNumbersBedsOptions,
                maxValues: maxNumbersBedsOptions,
            },
        ];
    } else if (key === 'price') {
        return [
            key,
            {
                ...value,
                type: isTheFilterFreeform(categories) ? 'range_freeform' : 'enum_range',
                minKey: `min_${key}`,
                maxKey: `max_${key}`,
                minValues: isTheFilterFreeform(categories) ? [] : getMinSelectList(categories),
                maxValues: isTheFilterFreeform(categories) ? [] : getMaxSelectList(categories),
            },
        ];
    } else if (
        key === ServerFilters.dog_breed ||
        key === ServerFilters.cat_breed ||
        key === ServerFilters.fashion_brand
    ) {
        return [
            key,
            {
                ...value,
                type: 'searchable-enum',
            },
        ];
    } else if (key === 'pet_dob') {
        return [
            key,
            {
                ...value,
                minKey: `min_${key}`,
                maxKey: `max_${key}`,
            },
        ];
    } else if (
        (
            [
                ServerFilters.deflead,
                ServerFilters.neutered_or_spayed,
                ServerFilters.vaccinated,
                ServerFilters.microchipped,
            ] as FilterKey[]
        ).includes(key)
    ) {
        return [
            key,
            {
                ...value,
                type: 'boolean-pets-health',
            },
        ];
    } else {
        return [key, value];
    }
};

const getFiltersDictionary = (data): FiltersDictionary => {
    const rawFilters = get('model.refinePanelModel.filters', data) as RefinePanelModel['filters'];

    return Object.fromEntries(
        Object.entries(rawFilters)
            .filter(([_, value]) => value.filterData[0])
            .map(
                ([key, value]) =>
                    [key, { ...value.filterData[0], label: value.label }] as [FilterKey, FilterData]
            )
    ) as FiltersDictionary;
};

export default getFiltersDictionary;
